import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Anchor, ArrowRight, Camera, Map, Users, Truck, FileText, RefreshCw, Clock, Check, Mail, User, Send, Loader2, Download,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1589730349861-f17e3939c207?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const CHEF = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FISH = "https://images.unsplash.com/photo-1582359425634-e3ded9a93770?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const KITCHEN = "https://images.unsplash.com/photo-1769955817432-641929f613f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FEATURES = [
  { n: "01", icon: Camera, title: "Fotografi la bolla, l'app la legge", desc: "Scatta una foto della fattura: l'AI la legge, aggiorna le bolle in automatico e costruisce lo storico di ogni fornitore." },
  { n: "02", icon: Map, title: "Storico fornitori e mappa dei prezzi", desc: "Ogni fattura confronta i prezzi e crea la mappatura del mercato: ortofrutta, pesce, carne, secchi. Tutto sotto controllo." },
  { n: "03", icon: Users, title: "Costo per coperto in tempo reale", desc: "Aggiorni i coperti del giorno e vedi subito il costo per persona, senza calcoli a mano." },
  { n: "04", icon: Truck, title: "Ordini ai fornitori, consegne rapide", desc: "Prepari la lista, invii l'ordine al fornitore in un tocco e ottieni consegne rapide." },
];

const EXTRAS = [
  { icon: FileText, title: "Report PDF mensile", desc: "Un report pulito, con grafici e trend, da consegnare al comandante o all'armatore." },
  { icon: RefreshCw, title: "Tutto sincronizzato", desc: "Telefono, tablet, computer: i tuoi dati sempre allineati, anche offline." },
];

export default function Landing() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Inserisci nome ed email");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", { name: name.trim(), email: email.trim() });
      setSent(true);
      toast.success("Richiesta inviata!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] font-sans text-navy" data-testid="landing-page">
      {/* NAV */}
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Yacht Food Cost" className="h-11 w-11 rounded-xl shadow-lg" />
            <span className="font-head text-lg font-bold tracking-tight text-white">YACHT FOOD COST</span>
          </div>
          <button data-testid="landing-login-cta" onClick={goLogin} className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-gold-hover">
            Accedi
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-navy-deep">
        <img src={HERO} alt="yacht" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy-deep/40" />
        <div className="animate-slide-up relative mx-auto w-full max-w-6xl px-5 py-28">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-gold">
              <Anchor className="h-3.5 w-3.5" /> Programma Beta 2026
            </span>
            <h1 className="mt-6 font-head text-5xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Yacht<br />
              <span className="relative inline-block text-gold">
                Food Cost
                <span className="absolute -bottom-2 left-0 h-1 w-full rounded-full bg-gold/70" />
              </span>
            </h1>
            <p className="mt-8 font-head text-xl font-medium text-white/90 sm:text-2xl">Costruito a bordo, non in ufficio.</p>
            <p className="mt-3 text-base text-slate-300">
              Per chi sta in cambusa. Basta fogli, fatture e calcoli a mano che ti rubano ore al sonno, al riposo e alla cucina.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="landing-hero-login" onClick={goLogin} className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-bold text-black transition-colors hover:bg-gold-hover">
                Entra nell'app <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <a data-testid="landing-features-anchor" href="#funzioni" className="flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-white/10">
                Scopri le funzioni
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section id="video" className="bg-[#F4F1EA] pt-20 lg:pt-28">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Guarda il video</p>
          <h2 className="mt-4 font-head text-3xl font-bold text-navy sm:text-4xl">Yacht Food Cost, in un minuto</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-navy/10 bg-navy shadow-2xl">
            <video data-testid="landing-promo-video" controls preload="metadata" playsInline poster={HERO} className="h-auto w-full">
              <source src="/promo.mp4" type="video/mp4" />
              Il tuo browser non supporta la riproduzione del video.
            </video>
          </div>
          <a data-testid="landing-download-video" href="/promo.mp4" download="yacht-food-cost.mp4" className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-hover">
            <Download className="h-4 w-4" /> Scarica il video
          </a>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="mx-auto max-w-6xl px-5 py-20 lg:py-28">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-2xl">
            <img src={CHEF} alt="Chef al lavoro" className="h-[420px] w-full object-cover" />
          </div>
          <div>
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Pensato da chi ci lavora</p>
            <blockquote className="mt-5 font-head text-3xl font-bold leading-snug text-navy sm:text-4xl">
              "Uno strumento pensato da chi sta in cambusa, per chi sta in cambusa."
            </blockquote>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Mi chiamo Tommaso Marin. Dopo 25 anni nelle cucine ho capito una cosa semplice: noi chef marinai
              abbiamo bisogno di uno strumento che ci accompagni e allevi il peso del controllo fatture e della
              gestione del food cost.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy font-head font-bold text-gold">TM</div>
              <div>
                <div className="font-semibold text-navy">Tommaso Marin</div>
                <div className="text-sm text-slate-500">Chef · 25 anni in cucina</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="funzioni" className="bg-navy py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="max-w-2xl">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Cosa fa per te</p>
            <h2 className="mt-4 font-head text-4xl font-bold text-white sm:text-5xl">Tutto sotto controllo, dal primo scatto</h2>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.n} data-testid={`landing-feature-${f.n}`} className="group rounded-2xl border border-white/10 bg-white/5 p-7 transition-colors hover:border-gold/40 hover:bg-white/[0.08]">
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <span className="font-head text-3xl font-bold text-white/15 transition-colors group-hover:text-gold/40">{f.n}</span>
                </div>
                <h3 className="mt-5 font-head text-xl font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {EXTRAS.map((e) => (
              <div key={e.title} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold">
                  <e.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-head text-lg font-semibold text-white">{e.title}</h3>
                  <p className="text-sm text-slate-300">{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="grid grid-cols-2 md:grid-cols-3">
        {[FISH, KITCHEN, CHEF].map((src, i) => (
          <div key={i} className={`relative h-52 overflow-hidden md:h-64 ${i === 2 ? "col-span-2 md:col-span-1" : ""}`}>
            <img src={src} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
      </section>

      {/* RICHIEDI ACCESSO */}
      <section id="accesso" className="bg-[#F4F1EA] py-20 lg:py-28">
        <div className="mx-auto max-w-xl px-5">
          <div className="rounded-2xl border border-navy/10 bg-white p-8 shadow-xl">
            <p className="font-head text-sm font-semibold uppercase tracking-widest text-gold">Programma Beta 2026</p>
            <h2 className="mt-3 font-head text-3xl font-bold text-navy">Richiedi l'accesso</h2>
            <p className="mt-2 text-sm text-slate-500">Lascia nome ed email: ti avvisiamo appena Yacht Food Cost è pronto per te.</p>
            {sent ? (
              <div data-testid="lead-success" className="mt-6 flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-4 font-medium text-emerald-700">
                <Check className="h-5 w-5" /> Grazie! Ti contatteremo presto.
              </div>
            ) : (
              <form onSubmit={submitLead} className="mt-6 space-y-3">
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="lead-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
                </div>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input data-testid="lead-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
                </div>
                <button data-testid="lead-submit-btn" type="submit" disabled={sending} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy font-bold text-white transition-colors hover:bg-navy-hover disabled:opacity-60">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Richiedi l'accesso
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy-deep py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <Clock className="mx-auto h-10 w-10 text-gold" />
          <h2 className="mt-6 font-head text-3xl font-bold leading-snug text-white sm:text-4xl">
            Il tempo è la cosa più preziosa a bordo. Yacht Food Cost te lo restituisce.
          </h2>
          <p className="mt-5 font-head text-xl text-gold">Costruito a bordo, non in ufficio.</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="landing-final-login" onClick={goLogin} className="group flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-bold text-black transition-colors hover:bg-gold-hover">
              Accedi all'app <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <a data-testid="landing-final-lead" href="#accesso" className="flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10">
              Richiedi l'accesso
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Foto delle bolle con AI</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Costo per coperto</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Report per l'armatore</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-deep py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Yacht Food Cost" className="h-8 w-8 rounded-lg" />
            <span className="font-head font-semibold text-white">YACHT FOOD COST</span>
          </div>
          <span>yachtfoodcost.com · Per chi sta in cambusa</span>
        </div>
      </footer>
    </div>
  );
}
